import { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import StatCard from "@/components/StatCard";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Target, AlertOctagon } from "lucide-react";

export default function MyQuality() {
  const [stats, setStats] = useState(null);
  const [charts, setCharts] = useState([]);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data));
    api.get("/charts", { params: { status: "completed" } }).then((r) => setCharts(r.data));
  }, []);

  if (!stats) return <div className="p-10 text-slate-500">Loading…</div>;

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto" data-testid="quality-page">
      <PageHeader title="My Quality" subtitle="Accuracy and error categories from QA audits." />

      <div className="grid sm:grid-cols-2 gap-5 mb-6">
        <StatCard label="Accuracy" value={`${stats.accuracy}%`} icon={Target} accent="text-sky-700" testId="quality-accuracy"/>
        <StatCard label="Completed Audits" value={stats.qa_completed} icon={AlertOctagon} testId="quality-completed"/>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5 mb-6">
        <h3 className="font-heading text-lg font-medium text-slate-900 mb-4">Error categories</h3>
        {stats.error_trends.length === 0 ? (
          <div className="text-sm text-slate-500 py-8 text-center">No errors recorded yet. Keep it up.</div>
        ) : (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.error_trends}>
                <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" vertical={false}/>
                <XAxis dataKey="category" tick={{ fontSize: 12, fill: "#64748B" }} />
                <YAxis tick={{ fontSize: 12, fill: "#64748B" }} allowDecimals={false}/>
                <Tooltip />
                <Bar dataKey="count" fill="#EF4444" radius={[6,6,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <h3 className="font-heading text-lg font-medium text-slate-900 mb-4">Recent QA results</h3>
        {charts.length === 0 ? (
          <div className="text-sm text-slate-500 py-8 text-center">No audits yet.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {charts.slice(0,10).map((c) => (
              <li key={c.id} className="py-3 flex items-center justify-between" data-testid={`quality-row-${c.chart_id}`}>
                <div>
                  <div className="font-medium text-slate-900">{c.chart_id} · {c.patient_name}</div>
                  <div className="text-xs text-slate-500">{c.audit?.qa_name} • {c.audit?.errors?.length || 0} error(s)</div>
                </div>
                <div className={`text-sm font-semibold ${c.audit?.score >= 95 ? "text-emerald-600" : c.audit?.score >= 85 ? "text-amber-600" : "text-red-600"}`}>
                  {c.audit?.score}%
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
